import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { LayoutService } from 'app/layout/layout.service';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { capitalizeFirstLetter } from 'app/shared/Utils/common.utils';
import { Observable, debounceTime, of } from 'rxjs';
import { CommonAction, TaskRole, TaskRolesResolverData, routeTypeMap } from './task-roles.types';
import { RelatedToType } from 'app/shared/enums';
import { TaskRolesState } from './state/state';
import { Select, Store } from '@ngxs/store';
import { TaskRolesActions } from './state/actions';
import { AccountService } from 'app/core';
import { Account } from 'app/core/models';

@Component({
  selector: 'app-task-roles',
  templateUrl: './task-roles.component.html',
  styleUrls: ['./task-roles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskRolesComponent extends OnDestroyMixin implements OnInit {
  ui = { isLoading$: of(false) };
  customObjects: Partial<CustomObject>[];
  taskRoles: TaskRole[];
  relatedToTypes: RelatedToType[];
  selectedType: string;
  searchInput = new UntypedFormControl('');

  @Select(TaskRolesState.get) taskRoles$: Observable<TaskRole[]>;

  get isLoading$(): Observable<boolean> {
    return this.ui.isLoading$;
  }

  private get isOnRootRouteLevel(): boolean {
    return this.router.url.endsWith('task-roles');
  }

  private get typeParam(): string {
    const [, typeParam] = this.router.url.split('task-roles/');
    return routeTypeMap[typeParam] ?? capitalizeFirstLetter(typeParam);
  }

  private get account(): Account {
    return this.accountService.getCurrentAccount();
  }

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly layoutService: LayoutService,
    private readonly store: Store,
    private readonly accountService: AccountService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.handleCommonSubjects();
    this.activatedRoute.data
      .pipe(untilComponentDestroyed(this))
      .subscribe(({ taskRolesData }: { taskRolesData: TaskRolesResolverData }) => {
        this.customObjects = taskRolesData.customObjects;
        this.relatedToTypes = taskRolesData.relatedToTypes;

        if (this.isOnRootRouteLevel) {
          const [firstType] = this.relatedToTypes;
          this.selectedType = firstType;
          this.router.navigate([firstType.toLowerCase()], { relativeTo: this.activatedRoute });
        } else {
          this.selectedType = this.typeParam;
        }
      });

    this.taskRoles$.pipe(untilComponentDestroyed(this)).subscribe((taskRoles) => {
      this.taskRoles = taskRoles;
      this.changeDetectorRef.markForCheck();
    });

    this.searchInput.valueChanges
      .pipe(untilComponentDestroyed(this), debounceTime(300))
      .subscribe((value: string) => this.layoutService.emitCommon(CommonAction.roleSearch, value, 'subject'));

    this.ui.isLoading$ = this.layoutService
      .getCommon<boolean>(CommonAction.setLoading)
      .pipe(untilComponentDestroyed(this));

    this.store.dispatch(new TaskRolesActions.Get(+this.account.id));
  }

  addRole(): void {
    this.layoutService.emitCommon(CommonAction.addRole, 'create', 'subject');
  }

  private handleCommonSubjects(): void {
    this.layoutService.emitCommon(CommonAction.roleSearch, '', 'subject');
    this.layoutService.emitCommon(CommonAction.addRole, '', 'subject');
    this.layoutService.emitCommon(CommonAction.setLoading, false, 'subject');
  }
}
